import { Box, CircularProgress } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import classNames from 'classnames';
import { Fragment, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import Container from '/@/components/container';
import PrivacyDisclaimer from '/@/components/privacy-disclaimer';
import settings from '/@/core/settings';
import { useAllSoyMultipliers, useSoyMultipliersZones } from '/@/hooks/soy-multipliers';
import { _ } from '/@/locale';
import { setCatalogZone as setCatalogZoneAction } from '/@/store/catalog/actions';
import { useCatalogZones } from '/@/store/catalog/apiProviders';
import { getCatalogZone } from '/@/store/catalog/selectors';
import { getSoyBrands } from '/@/store/multipliers/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import { reverse } from '/@/utils/router';
import Select from '/@/v3/components/forms/select';
import Title from '/@/v3/components/title';
import CatalogProduct from '/@/views/catalog/product-selection/product';
import styles from './styles.module.css';

const SoyMultipliers = () => {
    const dispatch = useDispatch();
    const history = useNavigate();
    const brands = useSelector(getSoyBrands);
    const brand = brands[0];
    const country = useSelector(getSessionCountry);

    const catalogZonesQuery = useCatalogZones({ page: 1, pageSize: 10000, crop: 'soy', type: 'multiplier' });
    const catalogZones = catalogZonesQuery.data?.results || [];

    const zone = useSelector((state) => getCatalogZone(state, catalogZones));
    const { data: zones, isValidating: areZonesLoading } = useSoyMultipliersZones(country);
    const setCatalogZone = bindActionCreators(setCatalogZoneAction, dispatch);
    const { data: items, isValidating: isLoading } = useAllSoyMultipliers(zone && zone.id);

    useEffect(() => {
        if (zones && zones.length === 0) return history(reverse('Home'));
        if (zones) setCatalogZone(zones?.[0]?.id);
    }, [zones]);

    const handleItemSelection = (profileId) => {
        history(
            reverse('SoyMultipliersDetail', {
                params: {
                    type: 'soy',
                    brand: brand.name,
                    zoneId: zone.id,
                    profileId,
                },
            })
        );
    };

    return (
        <Fragment>
            <Container classNameRoot={styles.rootTitle} className={styles.wrapper}>
                <div className={styles.containerTitle}>
                    <Title bolder className={styles.title}>
                        {_('Propuesta técnica de valor exclusivo para la multiplicación de semillas')}
                    </Title>
                    <Title className={styles.subtitle}>{_('¡Innovación a tu alcance!')}</Title>
                    <Title className={styles.bajada}>
                        {_(
                            'Encontrá toda la información sobre la genética más avanzada y la mejor biotecnología del mercado.'
                        )}
                    </Title>
                </div>
            </Container>
            <Fragment>
                <Container classNameRoot={styles.rootZones} className={styles.wrapper}>
                    <div className={styles.zones}>
                        <Title className={styles.zonesSubtitle} color='primary' bolder>
                            {_('Elegí la zona')}
                        </Title>
                        <div className={styles.zoneSelector}>
                            {areZonesLoading ? (
                                <CircularProgress size={24} />
                            ) : (
                                <Select
                                    color={settings.app.theme}
                                    value={zone && zone.id}
                                    options={zones.map((zone) => ({ label: zone.name, value: zone.id }))}
                                    clearable={false}
                                    onChange={setCatalogZone}
                                />
                            )}
                        </div>
                    </div>
                </Container>
                <Container
                    classNameRoot={styles.rootContent}
                    className={classNames(styles.wrapperContent, styles.wrapper)}
                >
                    <div className={styles.content}>
                        <div className={styles.items}>
                            {isLoading ? (
                                <Box sx={{ display: 'grid', gridTemplateColumns: { md: 'repeat(2, 1fr)' }, gap: 2 }}>
                                    {Array.from({ length: 6 }, (_, i) => (
                                        <Skeleton key={i} variant='rectangular' width='100%' height={300} />
                                    ))}
                                </Box>
                            ) : (
                                items &&
                                !!items.length && (
                                    <div className={styles.grid}>
                                        {items.map((item) => {
                                            return (
                                                <div key={item.id} className={classNames(styles.item, styles.soy)}>
                                                    <CatalogProduct
                                                        profile={item.product}
                                                        brand={brand}
                                                        handleClick={handleItemSelection}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </Container>

                <Container className={styles.wrapper}>
                    <PrivacyDisclaimer
                        crop='soy'
                        message={_(
                            'La siguiente información está destinada exclusivamente a la multiplicación de semillas, y contiene información específica para estos entornos y propósito, además representa solo una tendencia de comportamiento de estos cultivos. En algunos casos, es importante tener en cuenta que el posicionamiento será diferente al de las regiones en donde los cultivos están posicionados comercialmente.'
                        )}
                        warning={_('Por favor no compartir con terceros.')}
                        title={_('Contenido sensible para multiplicadores de semillas.')}
                    />
                </Container>
            </Fragment>
        </Fragment>
    );
};

export default SoyMultipliers;
